import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    ImageRun,
    Table,
    TableRow,
    TableCell,
    AlignmentType,
    VerticalAlign,
    WidthType,
    HeightRule,
    BorderStyle,
    ShadingType,
    UnderlineType,
    convertInchesToTwip,
    LevelFormat,
    AlignmentType as NumberAlignment,
} from "docx";
import { $getRoot, $isElementNode, $isTextNode } from "lexical";
import {
    $isListNode,
    $isListItemNode,
} from "@lexical/list";
import {
    $isHeadingNode,
    $isQuoteNode,
} from "@lexical/rich-text";
import {
    $isTableNode,
    $isTableRowNode,
    $isTableCellNode,
} from "@lexical/table";
import {
    $isLinkNode,
} from "@lexical/link";
import {
    $isCodeNode,
} from "@lexical/code";
import { $isLayoutContainerNode } from "../lexical/nodes/LayoutContainerNode";
import { $isLayoutItemNode } from "../lexical/nodes/LayoutItemNode";

/**
 * Enhanced DOCX Export Utility
 * 
 * IMPORTANT FIX (December 2025):
 * - Changed from async to synchronous processing because Lexical's getEditorState().read() 
 *   is synchronous, not async
 * - This was causing empty DOCX files because async/await was preventing proper node traversal
 * - All node processing methods are now synchronous (handleXNodeSync, extractTextRunsSync, etc.)
 * - Images from URLs are handled as placeholders in sync mode (base64 images work fine)
 * - Added checklist support with checkbox symbols (☑/☐) for checked/unchecked items
 * - Added complete heading theme styles (H1-H6) with colors, sizes, bold, and uppercase
 * - Added quote block styling with theme colors and border
 * - Added strikethrough for checked checklist items
 * - Added text alignment and indentation support for all paragraph types
 * 
 * Supports full formatting preservation including:
 * - Text formatting (bold, italic, underline, strikethrough, colors, fonts, sizes, subscript, superscript)
 * - Headings (H1-H6 with complete theme styling: colors, sizes, bold, uppercase)
 * - Lists (ordered, unordered, and checklists with proper nesting and strikethrough for checked items)
 * - Tables with borders, cell backgrounds, and styling
 * - Images (base64 embedded, URL images show as placeholders)
 * - Links (blue color with underline)
 * - Code blocks (monospace font with background)
 * - Quotes (themed colors, indentation, and left border)
 * - Text alignment (left, center, right, justify)
 * - Indentation support
 * - Page layout and spacing
 * - Theme support (light/dark)
 */

export class DocxExportHandler {
    constructor(editor, options = {}) {
        this.editor = editor;
        this.options = {
            pageSize: options.pageSize || 'LETTER',
            margins: options.margins || {
                top: convertInchesToTwip(1),
                right: convertInchesToTwip(1),
                bottom: convertInchesToTwip(1),
                left: convertInchesToTwip(1),
            },
            theme: options.theme || 'light',
            ...options,
        };
        this.imageCache = new Map();
        this.listCounters = new Map();
    }

    /**
     * Main export function
     */
    async exportToDocx(fileName) {
        const children = [];
        const debugMode = false; // Set to true for debugging
        
        try {
            // First pass: collect all image URLs that need to be fetched
            const imageUrlsToFetch = [];
            this.editor.getEditorState().read(() => {
                const root = $getRoot();
                this.collectImageUrls(root, imageUrlsToFetch);
            });

            // Fetch all URL images and convert to base64
            if (imageUrlsToFetch.length > 0) {
                await this.prefetchImages(imageUrlsToFetch);
            }

            // Second pass: process the document
            this.editor.getEditorState().read(() => {
                const root = $getRoot();
                
                // Traverse the root's children
                const rootChildren = root.getChildren();
                for (const child of rootChildren) {
                    this.processNodeSync(child, children, { level: 0 });
                }
            });

            if (debugMode) {
                console.warn('DOCX Export: Total paragraphs/elements:', children.length);
            }

            // If no content was extracted, add a placeholder
            if (children.length === 0) {
                console.warn('DOCX Export: No content found in document, creating empty document');
                children.push(new Paragraph({ 
                    children: [new TextRun("")]
                }));
            }

            // Create proper numbering configuration
            const numbering = {
                config: [
                    {
                        reference: "default-numbering",
                        levels: [
                            {
                                level: 0,
                                format: LevelFormat.DECIMAL,
                                text: "%1.",
                                alignment: AlignmentType.START,
                                style: {
                                    paragraph: {
                                        indent: { left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.25) },
                                    },
                                },
                            },
                            {
                                level: 1,
                                format: LevelFormat.DECIMAL,
                                text: "%2.",
                                alignment: AlignmentType.START,
                                style: {
                                    paragraph: {
                                        indent: { left: convertInchesToTwip(1.0), hanging: convertInchesToTwip(0.25) },
                                    },
                                },
                            },
                            {
                                level: 2,
                                format: LevelFormat.DECIMAL,
                                text: "%3.",
                                alignment: AlignmentType.START,
                                style: {
                                    paragraph: {
                                        indent: { left: convertInchesToTwip(1.5), hanging: convertInchesToTwip(0.25) },
                                    },
                                },
                            },
                        ],
                    },
                ],
            };

            const doc = new Document({
                numbering: numbering,
                styles: {
                    default: {
                        document: {
                            run: {
                                font: "Arial",
                                size: 22, // 11pt default
                            },
                            paragraph: {
                                spacing: {
                                    after: 120,
                                    line: 276,
                                },
                            },
                        },
                    },
                },
                sections: [{
                    properties: {
                        page: {
                            margin: this.options.margins,
                        },
                    },
                    children: children,
                }],
            });

            return await Packer.toBlob(doc);
        } catch (error) {
            console.error('DOCX Export Error:', error);
            throw error;
        }
    }

    /**
     * Synchronous version to process nodes (Lexical read() is synchronous)
     */
    processNodeSync(node, docxChildren, context = {}) {
        if (!node) return;

        const nodeType = node.getType();

        try {
            // Handle different node types
            if ($isHeadingNode(node)) {
                this.handleHeadingNodeSync(node, docxChildren, context);
            } else if (nodeType === 'paragraph') {
                this.handleParagraphNodeSync(node, docxChildren, context);
            } else if ($isListNode(node)) {
                this.handleListNodeSync(node, docxChildren, context);
            } else if ($isListItemNode(node)) {
                this.handleListItemNodeSync(node, docxChildren, context);
            } else if ($isTableNode(node)) {
                this.handleTableNodeSync(node, docxChildren, context);
            } else if ($isQuoteNode(node)) {
                this.handleQuoteNodeSync(node, docxChildren, context);
            } else if ($isCodeNode(node)) {
                this.handleCodeNodeSync(node, docxChildren, context);
            } else if (nodeType === 'image' || nodeType === 'inline-image') {
                this.handleImageNodeSync(node, docxChildren, context);
            } else if (nodeType === 'layout-container' || $isLayoutContainerNode(node)) {
                // Layout containers hold layout items, process children recursively
                const children = node.getChildren();
                for (const child of children) {
                    this.processNodeSync(child, docxChildren, context);
                }
            } else if (nodeType === 'layout-item' || $isLayoutItemNode(node)) {
                // Layout items contain the actual content, process children recursively
                const children = node.getChildren();
                for (const child of children) {
                    this.processNodeSync(child, docxChildren, context);
                }
            } else if (nodeType === 'horizontalrule') {
                this.handleHorizontalRule(docxChildren);
            } else if (nodeType === 'pagebreak') {
                this.handlePageBreak(docxChildren);
            } else if ($isElementNode(node)) {
                // Recursively process element nodes
                const children = node.getChildren();
                for (const child of children) {
                    this.processNodeSync(child, docxChildren, context);
                }
            }
        } catch (error) {
            console.error(`Error processing node type ${nodeType}:`, error, node);
            // Continue processing other nodes
        }
    }

    /**
     * Handle heading nodes (H1-H6)
     */
    handleHeadingNodeSync(node, docxChildren, context) {
        const tag = node.getTag();
        const headingLevelMap = {
            'h1': HeadingLevel.HEADING_1,
            'h2': HeadingLevel.HEADING_2,
            'h3': HeadingLevel.HEADING_3,
            'h4': HeadingLevel.HEADING_4,
            'h5': HeadingLevel.HEADING_5,
            'h6': HeadingLevel.HEADING_6,
        };

        const children = node.getChildren();
        
        // Check if heading contains images
        let hasImages = false;
        for (const child of children) {
            const childType = child.getType();
            if (childType === 'image' || childType === 'inline-image') {
                hasImages = true;
                // Get alignment from heading for the image
                const alignment = this.getAlignment(node);
                // Process image directly with heading's alignment
                this.handleImageNodeSync(child, docxChildren, { ...context, imageAlignment: alignment });
            }
        }

        // Apply theme colors, sizes, and styles to headings based on tag
        // These match the PlaygroundEditorTheme.css
        const themeColors = {
            'h1': '050505', // rgb(5, 5, 5) - almost black
            'h2': '65676B', // rgb(101, 103, 107) - gray
        };
        
        const themeSizes = {
            'h1': 48, // 24px = 24pt = 48 half-points
            'h2': 30, // 15px = 15pt = 30 half-points
            'h3': 24, // 12px = 12pt = 24 half-points
            'h4': 22, // 11px = 11pt = 22 half-points
            'h5': 20, // 10px = 10pt = 20 half-points
            'h6': 18, // 9px = 9pt = 18 half-points
        };
        
        const themeBold = {
            'h2': true, // font-weight: 700
        };
        
        const themeAllCaps = {
            'h2': true,
            'h3': true,
            'h4': true,
            'h5': true,
            'h6': true,
        };
        
        // Create heading context with theme styling
        const headingContext = {
            ...context,
            headingColor: themeColors[tag],
            headingSize: themeSizes[tag],
            headingBold: themeBold[tag],
            headingAllCaps: themeAllCaps[tag],
        };
        
        // Extract text runs with heading styling applied
        const finalTextRuns = this.extractTextRunsSync(node, headingContext);
        
        // Only add heading paragraph if it has text content
        if (finalTextRuns.length > 0) {
            const children = finalTextRuns.length > 0 ? finalTextRuns : [new TextRun("")];
            
            const alignment = this.getAlignment(node);
            const indent = this.getIndentation(node);
            
            const paragraphOptions = {
                heading: headingLevelMap[tag] || HeadingLevel.HEADING_1,
                children: children,
                alignment: alignment,
                spacing: {
                    before: 240,
                    after: 120,
                },
            };
            
            if (indent) {
                paragraphOptions.indent = indent;
            }
            
            docxChildren.push(new Paragraph(paragraphOptions));
        } else if (hasImages && textRuns.length === 0) {
            // If heading has only images, add empty heading to maintain structure
            docxChildren.push(
                new Paragraph({
                    heading: headingLevelMap[tag] || HeadingLevel.HEADING_1,
                    children: [new TextRun("")],
                    spacing: {
                        before: 240,
                        after: 120,
                    },
                })
            );
        }
    }

    /**
     * Handle paragraph nodes
     */
    handleParagraphNodeSync(node, docxChildren, context) {
        const children = node.getChildren();
        
        // Get paragraph style for inheritance
        const paragraphStyle = node.getStyle ? node.getStyle() : '';
        
        // Check if paragraph contains images
        let hasImages = false;
        for (const child of children) {
            const childType = child.getType();
            if (childType === 'image' || childType === 'inline-image') {
                hasImages = true;
                // Get alignment from paragraph for the image
                const alignment = this.getAlignment(node);
                // Process image directly with paragraph's alignment
                this.handleImageNodeSync(child, docxChildren, { ...context, imageAlignment: alignment });
            }
        }
        
        // If paragraph has images, we've already processed them
        // Still process text content if any
        const textRuns = this.extractTextRunsSync(node, { parentStyle: paragraphStyle });
        
        // Only add paragraph if it has text content
        if (textRuns.length > 0) {
            const alignment = this.getAlignment(node);
            const indent = this.getIndentation(node);
            const paragraphChildren = textRuns.length > 0 ? textRuns : [new TextRun("")];

            docxChildren.push(
                new Paragraph({
                    children: paragraphChildren,
                    alignment: alignment,
                    indent: indent,
                    spacing: {
                        before: 0,
                        after: 120,
                        line: 276, // 1.15 line spacing
                    },
                })
            );
        } else if (!hasImages) {
            // Empty paragraph with no images
            docxChildren.push(
                new Paragraph({
                    children: [new TextRun("")],
                    spacing: {
                        before: 0,
                        after: 120,
                        line: 276,
                    },
                })
            );
        }
    }

    /**
     * Handle list nodes
     */
    handleListNodeSync(node, docxChildren, context) {
        const listType = node.getListType();
        const newContext = {
            ...context,
            listType: listType, // 'bullet', 'number', or 'check'
            level: (context.level || 0),
        };

        const children = node.getChildren();
        for (const child of children) {
            this.processNodeSync(child, docxChildren, newContext);
        }
    }

    /**
     * Handle list item nodes
     */
    handleListItemNodeSync(node, docxChildren, context) {
        const level = context.level || 0;
        const listType = context.listType || 'bullet';

        // Get list item style for inheritance
        const listItemStyle = node.getStyle ? node.getStyle() : '';

        // Check if this is a checklist item
        const isChecked = node.getChecked();
        const isChecklistItem = isChecked !== undefined && isChecked !== null;

        // Check if list item contains a heading node
        const children = node.getChildren();
        let headingNode = null;
        let headingTag = null;
        
        for (const child of children) {
            if ($isHeadingNode(child)) {
                headingNode = child;
                headingTag = child.getTag();
                break;
            }
        }

        // If list item has a heading, extract text with heading styling
        let textRuns;
        if (headingNode) {
            // Apply heading theme styles
            const themeColors = {
                'h1': '050505',
                'h2': '65676B',
            };
            
            const themeSizes = {
                'h1': 48,
                'h2': 30,
                'h3': 24,
                'h4': 22,
                'h5': 20,
                'h6': 18,
            };
            
            const themeBold = {
                'h2': true,
            };
            
            const themeAllCaps = {
                'h2': true,
                'h3': true,
                'h4': true,
                'h5': true,
                'h6': true,
            };
            
            const headingContext = {
                ...context,
                headingColor: themeColors[headingTag],
                headingSize: themeSizes[headingTag],
                headingBold: themeBold[headingTag],
                headingAllCaps: themeAllCaps[headingTag],
                parentStyle: listItemStyle,
            };
            
            textRuns = this.extractTextRunsSync(headingNode, headingContext);
        } else {
            textRuns = this.extractTextRunsSync(node, { parentStyle: listItemStyle });
        }

        // For checklist items, prepend checkbox symbol
        let paragraphChildren = textRuns.length > 0 ? textRuns : [new TextRun("")];
        
        if (listType === 'check' || isChecklistItem) {
            // Add checkbox symbol at the beginning
            const checkboxSymbol = isChecked ? '☑' : '☐';
            
            // Apply strikethrough to checked items per PlaygroundEditorTheme
            if (isChecked) {
                paragraphChildren = paragraphChildren.map(run => {
                    return new TextRun({
                        ...run,
                        strike: true,
                    });
                });
            }
            
            paragraphChildren = [
                new TextRun({
                    text: checkboxSymbol + ' ',
                    size: 24, // 12pt
                }),
                ...paragraphChildren
            ];
        }

        const paragraphOptions = {
            children: paragraphChildren,
            spacing: {
                before: 0,
                after: 120,
            },
        };

        if (listType === 'number') {
            paragraphOptions.numbering = {
                reference: "default-numbering",
                level: level,
            };
        } else if (listType === 'check') {
            // For checklist, use indentation without bullet
            paragraphOptions.indent = {
                left: convertInchesToTwip(0.5 * (level + 1)),
            };
        } else {
            // For bullet lists, use simple indentation with bullet character
            paragraphOptions.bullet = {
                level: level,
            };
        }

        docxChildren.push(new Paragraph(paragraphOptions));

        // Process nested lists
        const nestedChildren = node.getChildren();
        for (const child of nestedChildren) {
            if ($isListNode(child)) {
                this.handleListNodeSync(child, docxChildren, {
                    ...context,
                    level: level + 1,
                });
            }
        }
    }

    /**
     * Handle table nodes
     */
    handleTableNodeSync(node, docxChildren, context) {
        const tableRows = [];
        const children = node.getChildren();

        for (const row of children) {
            if ($isTableRowNode(row)) {
                const tableCells = [];
                const rowChildren = row.getChildren();

                for (const cell of rowChildren) {
                    if ($isTableCellNode(cell)) {
                        const cellChildren = [];
                        const cellChildNodes = cell.getChildren();
                        
                        for (const cellChild of cellChildNodes) {
                            this.processNodeSync(cellChild, cellChildren, context);
                        }

                        tableCells.push(
                            new TableCell({
                                children: cellChildren.length > 0 ? cellChildren : [
                                    new Paragraph({ children: [new TextRun("")] })
                                ],
                                width: {
                                    size: 100 / rowChildren.length,
                                    type: WidthType.PERCENTAGE,
                                },
                                margins: {
                                    top: 100,
                                    bottom: 100,
                                    left: 100,
                                    right: 100,
                                },
                                verticalAlign: VerticalAlign.CENTER,
                                shading: this.getCellShading(cell),
                            })
                        );
                    }
                }

                if (tableCells.length > 0) {
                    tableRows.push(
                        new TableRow({
                            children: tableCells,
                            height: {
                                value: 400,
                                rule: HeightRule.AT_LEAST,
                            },
                        })
                    );
                }
            }
        }

        if (tableRows.length > 0) {
            docxChildren.push(
                new Table({
                    rows: tableRows,
                    width: {
                        size: 100,
                        type: WidthType.PERCENTAGE,
                    },
                    borders: {
                        top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                        bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                        left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                        right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                        insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                        insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    },
                })
            );
        }
    }

    /**
     * Handle quote nodes
     */
    handleQuoteNodeSync(node, docxChildren, context) {
        // Apply quote theme styling: color rgb(101, 103, 107) and font-size 15px
        const quoteContext = {
            ...context,
            quoteColor: '65676B',
            quoteSize: 30, // 15px = 15pt = 30 half-points
        };
        
        const textRuns = this.extractTextRunsSync(node, quoteContext);

        // Ensure we always have at least one TextRun
        const children = textRuns.length > 0 ? textRuns : [new TextRun("")];

        docxChildren.push(
            new Paragraph({
                children: children,
                indent: {
                    left: convertInchesToTwip(0.5),
                },
                spacing: {
                    before: 120,
                    after: 120,
                },
                border: {
                    left: {
                        color: "CED0D4", // rgb(206, 208, 212)
                        space: 1,
                        style: BorderStyle.SINGLE,
                        size: 6,
                    },
                },
            })
        );
    }

    /**
     * Handle code block nodes
     */
    handleCodeNodeSync(node, docxChildren, context) {
        const textContent = node.getTextContent();

        docxChildren.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: textContent,
                        font: "Consolas",
                        size: 20, // 10pt
                    }),
                ],
                shading: {
                    type: ShadingType.SOLID,
                    color: "F5F5F5",
                },
                spacing: {
                    before: 120,
                    after: 120,
                },
            })
        );
    }

    /**
     * Handle image nodes - Note: Images are handled synchronously, 
     * but URL fetching is skipped in sync mode
     */
    handleImageNodeSync(node, docxChildren, context) {
        try {
            // Access image properties - check both methods and direct properties
            const src = typeof node.getSrc === 'function' ? node.getSrc() : node.__src;
            const altText = (typeof node.getAltText === 'function' ? node.getAltText() : node.__altText) || "Image";
            const maxWidth = node.__maxWidth || 500;
            
            // Width and height might be stored differently
            let width = node.__width || 'inherit';
            let height = node.__height || 'inherit';

            if (!src) {
                return;
            }

            let imageData = null;

            if (src.startsWith('data:')) {
                // Base64 image
                const base64Data = src.split(',')[1];
                if (base64Data) {
                    imageData = base64Data;
                }
            } else if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) {
                // Check cache for prefetched URL images
                if (this.imageCache.has(src)) {
                    const cachedData = this.imageCache.get(src);
                    // Extract base64 part (after comma if it includes data URI prefix)
                    imageData = cachedData.includes(',') ? cachedData.split(',')[1] : cachedData;
                } else {
                    // URL image not in cache
                    docxChildren.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: `[Image: ${altText} - ${src}]`,
                                    italics: true,
                                    color: "999999",
                                }),
                            ],
                        })
                    );
                    return;
                }
            }

            if (imageData) {
                // Detect image type from base64 header or original src
                let imageType = 'png';
                if (src.startsWith('data:')) {
                    imageType = src.match(/data:image\/(.*?);/)?.[1] || 'png';
                } else if (this.imageCache.has(src)) {
                    // Try to detect from cached data URI
                    const cachedData = this.imageCache.get(src);
                    imageType = cachedData.match(/data:image\/(.*?);/)?.[1] || 'png';
                }
                
                // Convert base64 to Uint8Array for browser compatibility
                const binaryString = atob(imageData);
                const len = binaryString.length;
                const bytes = new Uint8Array(len);
                for (let i = 0; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                try {
                    // Get alignment from parent context (paragraph/heading)
                    const imageAlignment = context.imageAlignment || AlignmentType.LEFT;
                    
                    // Get the actual stored dimensions
                    let actualWidth = width === 'inherit' ? 500 : Number(width);
                    let actualHeight = height === 'inherit' ? 300 : Number(height);
                    
                    // Lexical applies max-width constraint in CSS, which scales both dimensions proportionally
                    // If original width exceeds maxWidth, BOTH dimensions are scaled down
                    if (maxWidth && actualWidth > maxWidth) {
                        const scale = maxWidth / actualWidth;
                        actualWidth = Math.round(actualWidth * scale);
                        actualHeight = Math.round(actualHeight * scale);
                    }
                    
                    const imageRun = new ImageRun({
                        data: bytes,
                        transformation: {
                            width: actualWidth,
                            height: actualHeight,
                        },
                        type: imageType === 'jpeg' || imageType === 'jpg' ? 'jpg' : imageType,
                    });

                    docxChildren.push(
                        new Paragraph({
                            children: [imageRun],
                            alignment: imageAlignment,
                            spacing: {
                                before: 120,
                                after: 120,
                            },
                        })
                    );
                } catch (imgError) {
                    // Add placeholder
                    docxChildren.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "[Image format error]",
                                    italics: true,
                                    color: "999999",
                                }),
                            ],
                        })
                    );
                }
            }
        } catch (error) {
            // Add placeholder text for failed images
            docxChildren.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: "[Image could not be loaded]",
                            italics: true,
                            color: "999999",
                        }),
                    ],
                })
            );
        }
    }

    /**
     * Handle horizontal rule
     */
    handleHorizontalRule(docxChildren) {
        docxChildren.push(
            new Paragraph({
                border: {
                    bottom: {
                        color: "000000",
                        space: 1,
                        style: BorderStyle.SINGLE,
                        size: 6,
                    },
                },
                spacing: {
                    before: 120,
                    after: 120,
                },
            })
        );
    }

    /**
     * Handle page break
     */
    handlePageBreak(docxChildren) {
        docxChildren.push(
            new Paragraph({
                pageBreakBefore: true,
            })
        );
    }

    /**
     * Extract text runs from a node with full formatting (synchronous)
     */
    extractTextRunsSync(node, context = {}) {
        const textRuns = [];
        const children = node.getChildren ? node.getChildren() : [];

        for (const child of children) {
            const childType = child.getType();
            
            // Skip image nodes - they're handled separately
            if (childType === 'image' || childType === 'inline-image') {
                continue;
            }
            
            if ($isTextNode(child)) {
                const textRun = this.createTextRun(child, context);
                textRuns.push(textRun);
            } else if ($isLinkNode(child)) {
                const linkChildren = child.getChildren();
                for (const linkChild of linkChildren) {
                    if ($isTextNode(linkChild)) {
                        const textRun = this.createTextRun(linkChild, {
                            ...context,
                            isLink: true,
                            url: child.getURL(),
                        });
                        textRuns.push(textRun);
                    }
                }
            } else if ($isElementNode(child)) {
                const nestedRuns = this.extractTextRunsSync(child, context);
                textRuns.push(...nestedRuns);
            }
        }

        return textRuns;
    }

    /**
     * Create a TextRun with full formatting
     */
    createTextRun(textNode, options = {}) {
        const text = textNode.getTextContent();
        
        const runOptions = {
            text: text,
            font: "Arial", // Default font
            size: 22, // Default 11pt (22 half-points)
        };

        // Apply parent styles first (can be overridden by theme and inline styles)
        if (options.parentStyle && options.parentStyle.trim()) {
            const parentStyleObj = this.parseStyleString(options.parentStyle);
            
            // Font family
            const fontFamily = parentStyleObj['font-family'] || parentStyleObj.fontFamily;
            if (fontFamily) {
                runOptions.font = fontFamily.replace(/['",]/g, '').trim();
            }
            
            // Font size
            const fontSize = parentStyleObj['font-size'] || parentStyleObj.fontSize;
            if (fontSize) {
                let size = parseFloat(fontSize);
                if (!isNaN(size)) {
                    if (fontSize.includes('px')) {
                        size = size * 0.75; // Convert px to pt
                    }
                    runOptions.size = Math.round(size * 2); // DOCX uses half-points
                }
            }
            
            // Text color
            const color = parentStyleObj['color'] || parentStyleObj.color;
            if (color) {
                const parsedColor = this.parseColor(color);
                if (parsedColor) {
                    runOptions.color = parsedColor;
                }
            }
            
            // Background color
            const bgColor = parentStyleObj['background-color'] || parentStyleObj.backgroundColor;
            if (bgColor) {
                const parsedBgColor = this.parseColor(bgColor);
                if (parsedBgColor) {
                    runOptions.shading = {
                        type: ShadingType.SOLID,
                        color: parsedBgColor,
                    };
                }
            }
        }

        // Apply heading theme defaults (can be overridden by inline styles)
        if (options.headingSize) {
            runOptions.size = options.headingSize;
        }
        if (options.headingColor) {
            runOptions.color = options.headingColor;
        }
        if (options.headingBold) {
            runOptions.bold = options.headingBold;
        }
        if (options.headingAllCaps) {
            runOptions.allCaps = true;
        }
        
        // Apply quote theme defaults
        if (options.quoteSize) {
            runOptions.size = options.quoteSize;
        }
        if (options.quoteColor) {
            runOptions.color = options.quoteColor;
        }

        // Check if node has format methods before calling them
        if (typeof textNode.hasFormat === 'function') {
            if (textNode.hasFormat('bold')) {
                runOptions.bold = true;
            }
            runOptions.italics = textNode.hasFormat('italic');
            runOptions.underline = textNode.hasFormat('underline') ? { type: UnderlineType.SINGLE } : undefined;
            runOptions.strike = textNode.hasFormat('strikethrough');
            runOptions.subScript = textNode.hasFormat('subscript');
            runOptions.superScript = textNode.hasFormat('superscript');
        }

        // Get style if available - can be from getStyle() or __style property
        let style = null;
        if (typeof textNode.getStyle === 'function') {
            style = textNode.getStyle();
        } else if (textNode.__style) {
            style = textNode.__style;
        }

        // Parse inline styles
        if (style && style.trim()) {
            const styleObj = this.parseStyleString(style);
            
            // Font family - check various property names
            const fontFamily = styleObj['font-family'] || styleObj.fontFamily;
            if (fontFamily) {
                runOptions.font = fontFamily.replace(/['\",]/g, '').trim();
            }

            // Font size - check various property names and handle units
            const fontSize = styleObj['font-size'] || styleObj.fontSize;
            if (fontSize) {
                // Handle different units: px, pt, em
                let size = parseFloat(fontSize);
                if (!isNaN(size)) {
                    // If it's in px, convert to pt first (1pt = 1.333px)
                    if (fontSize.includes('px')) {
                        size = size * 0.75; // Convert px to pt
                    }
                    // DOCX uses half-points
                    runOptions.size = Math.round(size * 2);
                }
            }

            // Text color
            const color = styleObj['color'] || styleObj.color;
            if (color) {
                const parsedColor = this.parseColor(color);
                if (parsedColor) {
                    runOptions.color = parsedColor;
                }
            }

            // Background color / highlight
            const bgColor = styleObj['background-color'] || styleObj.backgroundColor;
            if (bgColor) {
                const parsedBgColor = this.parseColor(bgColor);
                if (parsedBgColor) {
                    runOptions.shading = {
                        type: ShadingType.SOLID,
                        color: parsedBgColor,
                    };
                }
            }
        }

        // Handle links
        if (options.isLink && options.url) {
            runOptions.color = "0000FF";
            runOptions.underline = { type: UnderlineType.SINGLE };
        }

        return new TextRun(runOptions);
    }

    /**
     * Parse CSS style string to object
     */
    parseStyleString(styleString) {
        const styleObj = {};
        const styles = styleString.split(';');
        
        for (const style of styles) {
            const [key, value] = style.split(':').map(s => s.trim());
            if (key && value) {
                styleObj[key] = value;
            }
        }
        
        return styleObj;
    }

    /**
     * Parse color value to hex
     */
    parseColor(colorValue) {
        if (!colorValue) return null;

        // Handle hex colors
        if (colorValue.startsWith('#')) {
            return colorValue.slice(1).toUpperCase();
        }

        // Handle rgb colors
        if (colorValue.startsWith('rgb')) {
            const match = colorValue.match(/\d+/g);
            if (match && match.length >= 3) {
                const r = parseInt(match[0]).toString(16).padStart(2, '0');
                const g = parseInt(match[1]).toString(16).padStart(2, '0');
                const b = parseInt(match[2]).toString(16).padStart(2, '0');
                return (r + g + b).toUpperCase();
            }
        }

        // Named colors mapping
        const namedColors = {
            'black': '000000',
            'white': 'FFFFFF',
            'red': 'FF0000',
            'green': '008000',
            'blue': '0000FF',
            'yellow': 'FFFF00',
            'gray': '808080',
            'grey': '808080',
        };

        return namedColors[colorValue.toLowerCase()] || null;
    }

    /**
     * Get paragraph alignment
     */
    getAlignment(node) {
        const format = node.getFormatType ? node.getFormatType() : '';
        
        const alignmentMap = {
            'left': AlignmentType.LEFT,
            'center': AlignmentType.CENTER,
            'right': AlignmentType.RIGHT,
            'justify': AlignmentType.JUSTIFIED,
        };

        return alignmentMap[format] || AlignmentType.LEFT;
    }

    /**
     * Get paragraph indentation
     */
    getIndentation(node) {
        const indent = node.getIndent ? node.getIndent() : 0;
        
        if (indent > 0) {
            return {
                left: convertInchesToTwip(0.5 * indent),
            };
        }

        return undefined;
    }

    /**
     * Get table cell shading
     */
    getCellShading(cell) {
        const backgroundColor = cell.getBackgroundColor ? cell.getBackgroundColor() : null;
        
        if (backgroundColor) {
            const color = this.parseColor(backgroundColor);
            if (color) {
                return {
                    type: ShadingType.SOLID,
                    color: color,
                };
            }
        }

        return undefined;
    }

    /**
     * Collect all image URLs from the document that need to be fetched
     */
    collectImageUrls(node, urls) {
        if (!node) return;

        const nodeType = node.getType();
        
        // Check if it's an image node
        if (nodeType === 'image' || nodeType === 'inline-image') {
            const src = typeof node.getSrc === 'function' ? node.getSrc() : node.__src;
            if (src && (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/'))) {
                if (!urls.includes(src)) {
                    urls.push(src);
                }
            }
        }

        // Recursively check children
        const children = node.getChildren ? node.getChildren() : [];
        for (const child of children) {
            this.collectImageUrls(child, urls);
        }
    }

    /**
     * Prefetch all images and convert to base64
     */
    async prefetchImages(urls) {
        const promises = urls.map(url => this.fetchImageAsBase64(url));
        await Promise.all(promises);
    }

    /**
     * Fetch image from URL and convert to base64
     */
    async fetchImageAsBase64(url) {
        // Check cache first
        if (this.imageCache.has(url)) {
            return this.imageCache.get(url);
        }

        try {
            // Handle relative URLs
            const imageUrl = url.startsWith('/') ? window.location.origin + url : url;
            
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64 = reader.result;
                    this.imageCache.set(url, base64);
                    resolve(base64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Failed to fetch image:', url, error);
            return null;
        }
    }
}

/**
 * Export function to be used in the export component
 */
export async function exportEditorToDocx(editor, fileName, options = {}) {
    const handler = new DocxExportHandler(editor, options);
    return await handler.exportToDocx(fileName);
}
